import { InvoiceRecord } from '../types/invoice';
import { Reservation } from '../types/reservation';

export interface BillToInfo {
  key: string;
  displayName: string;
  contactPersonName?: string;
  email?: string;
  phoneNumber?: string;
  type: 'PERSON' | 'COMPANY' | 'UNKNOWN';
}

const hasText = (value?: string | null) => Boolean(value && value.trim());

const joinWords = (...parts: Array<string | null | undefined>) =>
  parts
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

const normalizeDisplayName = (value?: string | null, fallback = 'Unavailable') =>
  hasText(value) ? value!.trim() : fallback;

export const getReservationDriverName = (
  reservation: Pick<Reservation, 'driver' | 'guestDriverName'>
) => {
  if (reservation.driver) {
    return normalizeDisplayName(
      joinWords(reservation.driver.firstName, reservation.driver.lastName)
    );
  }

  return normalizeDisplayName(reservation.guestDriverName);
};

export const getReservationBillToInfo = (
  reservation: Pick<
    Reservation,
    | 'id'
    | 'driver'
    | 'customer'
    | 'guestDriverEmail'
    | 'guestDriverName'
    | 'guestDriverPhone'
    | 'customerSameAsDriver'
    | 'customerId'
  >
): BillToInfo => {
  if (reservation.customerSameAsDriver === false && reservation.customer) {
    const customer = reservation.customer;
    const displayName = normalizeDisplayName(customer.displayName);
    return {
      key:
        customer.id != null
          ? `customer:${customer.id}`
          : hasText(customer.email)
            ? `email:${customer.email!.trim().toLowerCase()}`
            : `name:${displayName.toLowerCase()}`,
      displayName,
      contactPersonName: customer.contactPersonName?.trim() || undefined,
      email: customer.email?.trim() || undefined,
      phoneNumber: customer.phoneNumber?.trim() || undefined,
      type: customer.type || 'COMPANY',
    };
  }

  if (reservation.driver) {
    const displayName = normalizeDisplayName(
      joinWords(reservation.driver.firstName, reservation.driver.lastName)
    );
    return {
      key:
        reservation.driver.id != null
          ? `driver:${reservation.driver.id}`
          : hasText(reservation.driver.email)
            ? `email:${reservation.driver.email.trim().toLowerCase()}`
            : `name:${displayName.toLowerCase()}`,
      displayName,
      email: reservation.driver.email?.trim() || undefined,
      phoneNumber: reservation.driver.phoneNumber?.trim() || undefined,
      type: 'PERSON',
    };
  }

  const guestName = normalizeDisplayName(reservation.guestDriverName);
  const guestEmail = reservation.guestDriverEmail?.trim();
  return {
    key: guestEmail ? `guest-email:${guestEmail.toLowerCase()}` : `guest:${guestName.toLowerCase()}:${reservation.id}`,
    displayName: guestName,
    email: guestEmail || undefined,
    phoneNumber: reservation.guestDriverPhone?.trim() || undefined,
    type: 'PERSON',
  };
};

export const getInvoiceBillToInfo = (
  invoice: Pick<
    InvoiceRecord,
    | 'id'
    | 'reservation'
    | 'billToSourceType'
    | 'billToSourceId'
    | 'billToType'
    | 'billToName'
    | 'billToContactPersonName'
    | 'billToEmail'
    | 'billToPhone'
  >
): BillToInfo => {
  if (hasText(invoice.billToName) || hasText(invoice.billToEmail) || hasText(invoice.billToPhone)) {
    const displayName = normalizeDisplayName(invoice.billToName);
    const normalizedSourceType = (invoice.billToSourceType || '').toString().toLowerCase();
    const key = invoice.billToSourceType && invoice.billToSourceId != null
      ? `${normalizedSourceType}:${invoice.billToSourceId}`
      : hasText(invoice.billToEmail)
        ? `email:${invoice.billToEmail!.trim().toLowerCase()}`
        : `invoice:${invoice.id}`;

    return {
      key,
      displayName,
      contactPersonName: invoice.billToContactPersonName?.trim() || undefined,
      email: invoice.billToEmail?.trim() || undefined,
      phoneNumber: invoice.billToPhone?.trim() || undefined,
      type:
        invoice.billToType === 'COMPANY' || invoice.billToType === 'PERSON'
          ? invoice.billToType
          : 'UNKNOWN',
    };
  }

  if (invoice.reservation) {
    return getReservationBillToInfo(invoice.reservation);
  }

  return {
    key: `invoice:${invoice.id}`,
    displayName: 'Unavailable',
    type: 'UNKNOWN',
  };
};
